import { ChevronDownIcon, GlobeAltIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchCountries } from "../../services/api";

interface ContriesDropdownProps {
    onLocationChange: (value: string) => void;
    value: string;
    /** Pass an already-loaded list to skip the extra /countries request */
    options?: string[];
    placeholder?: string;
    /** "figma" = grey filled 36px field used on Account settings → Profile */
    variant?: "default" | "figma";
    /** strict = only a country picked from the list can become the value (typing only filters) */
    strict?: boolean;
    hasError?: boolean;
}

export const ContriesDropdown: React.FC<ContriesDropdownProps> = ({
    onLocationChange,
    value,
    options,
    placeholder,
    variant = "default",
    strict = false,
    hasError = false,
}) => {
    const [fetched, setFetched] = useState<string[]>([]);
    const [inputValue, setInputValue] = useState(value || "");
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation("CreateEditOpportunities");
    const locations = options ?? fetched;
    const isFigma = variant === "figma";

    useEffect(() => {
        setInputValue(value || "");
    }, [value]);

    useEffect(() => {
        if (options) return;
        fetchCountries()
            .then(setFetched)
            .catch((err) => console.error("Failed to load countries", err));
    }, [options]);

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (event.target instanceof Node && containerRef.current?.contains(event.target)) return;

            setIsOpen(false);
            // strict mode: discard half-typed text that is not a real selection
            if (strict) setInputValue(value || "");
        };

        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, [strict, value]);

    // In strict mode only filter while the user is typing something different from the selection
    const isFiltering = inputValue.trim() !== "" && !(strict && inputValue === value);
    const filtered = isFiltering
        ? locations.filter((location) => location.toLowerCase().includes(inputValue.toLowerCase()))
        : locations;

    const handleSelect = (event: React.MouseEvent, country: string) => {
        event.preventDefault();
        setInputValue(country);
        onLocationChange(country);
        setIsOpen(false);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value);
        if (!strict) onLocationChange(event.target.value);
        setIsOpen(true);
    };

    const getFigmaWrapperClass = () => {
        if (hasError) return "border-red-400 bg-[#EBEBEC]";
        if (isOpen) return "border-blue-500 bg-white";
        return "border-transparent bg-[#EBEBEC]";
    };

    const wrapperClass = isFigma
        ? `flex h-9 min-w-0 items-center gap-2 rounded-xl border px-3 transition-colors ${getFigmaWrapperClass()}`
        : `flex h-8 min-w-0 items-center gap-2 rounded-lg border bg-white px-2 ${
              isOpen ? "border-blue-500 ring-2 ring-blue-100" : "border-gray-300"
          }`;

    return (
        <div ref={containerRef} className="relative w-full min-w-0 flex-1">
            <div className={wrapperClass}>
                {!isFigma && <GlobeAltIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />}
                <input
                    type="text"
                    value={inputValue}
                    placeholder={placeholder ?? t("selectLocation")}
                    onChange={handleChange}
                    onFocus={() => setIsOpen(true)}
                    autoComplete="off"
                    className={`min-w-0 flex-1 truncate bg-transparent outline-none ${
                        isFigma ? "text-sm text-gray-900 placeholder-gray-500" : "text-sm placeholder-gray-400"
                    }`}
                />
                <ChevronDownIcon
                    className={`flex-shrink-0 transition-transform ${
                        isFigma ? "h-4 w-4 text-gray-900" : "h-3.5 w-3.5 text-gray-400"
                    } ${isOpen ? "rotate-180" : ""}`}
                />
            </div>

            {isOpen && (
                <ul
                    className={`absolute z-50 mt-1 max-h-56 w-full overflow-y-auto border border-gray-200 bg-white py-1 text-sm shadow-lg ${
                        isFigma ? "rounded-xl" : "rounded-lg"
                    }`}
                >
                    {filtered.length > 0 ? (
                        filtered.map((country) => (
                            <li
                                key={country}
                                onMouseDown={(event) => handleSelect(event, country)}
                                className={`cursor-pointer px-3 py-1.5 rounded-xl hover:bg-[#EBEBEC] text-gray-900 ${
                                    country === value ? "bg-[#EBEBEC] text-gray-900" : "text-gray-900"
                                }`}
                            >
                                {country}
                            </li>
                        ))
                    ) : (
                        <li className="px-3 py-2 text-gray-400">{t("noResults", "No results found")}</li>
                    )}
                </ul>
            )}
        </div>
    );
};
